/**
 * @fileoverview Helper functions for using SelectEntities in the Taskflow.
 *
 * @author Wyatt Lindquist (wlindquist@yext.com)
 */
import {SelectEntities} from '/src/com/yext/listingslaunchstorm/js/enterprise/selectentities/selectentities';
import {EnterpriseConfiguration} from '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/data/enterpriseconfiguration';
import {Task} from '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/data/task';
import {TaskIds} from '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/data/taskids';

import * as styles from '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/taskflow.module.scss';
import BookSvg from '/src/com/yext/listingslaunchstorm/public/icons/book.svg';
import ListingsLargeSvg from '/src/com/yext/listingslaunchstorm/public/icons/listings-large.svg';

export const SelectEntitiesTask = new Task({
  id: TaskIds.SELECT_ENTITIES,

  getPage: (enterpriseConfiguration, setEnterpriseConfiguration, pageControls) => ({
    id: TaskIds.SELECT_ENTITIES,
    layoutHeader: {
      icon: (<ListingsLargeSvg />),
      title: yext.msg('Select Entities'),
      description: getHeaderDescription(),
      resourceLink: {
        src: 'https://hitchhikers.yext.com/tracks/knowledge-graph/',
        external: true,
        displayText: yext.msg('Learn About Managing Your Content'),
        icon: (<BookSvg />),
      },
    },
    content: (
      <SelectEntities
        selection={enterpriseConfiguration.entitySelection}
        onChangeSelection={entitySelection => {
          // Clear the feature selection as the new entity selection may have a larger count,
          // causing already selected license packs to become unavailable for the new count.
          pageControls.resetPage(TaskIds.SELECT_FEATURES);
          setEnterpriseConfiguration(
            new EnterpriseConfiguration({
              ...enterpriseConfiguration,
              entitySelection,
              featureSelection: undefined,
            }));
        }}
      />
    ),
  }),

  getCard: (enterpriseConfiguration, index, selectedPageId, setSelectedPageId, disabled) => ({
    id: TaskIds.SELECT_ENTITIES,
    title: yext.msg('Select Entities'),
    index,
    isSelected: (selectedPageId == TaskIds.SELECT_ENTITIES),
    onClick: disabled ? undefined : () => setSelectedPageId(TaskIds.SELECT_ENTITIES),
    content: getCardContent(enterpriseConfiguration),
  }),

  isComplete: enterpriseConfiguration => (enterpriseConfiguration.entitySelection !== undefined),
  isSectionSkipped: () => false,
});

/** @returns {React.ReactNode} */
function getHeaderDescription() {
  const message = yext.msg('Set up your Listings by selecting which entities to launch. If you are an enterprise with multiple brands, we recommend launching one brand at a time. You can separate each brand\'s entities by creating a custom <a href="{0}" target="_blank">saved filter</a> and then selecting it here.', 'https://hitchhikers.yext.com/tracks/knowledge-graph/kg131-saved-filters/');
  return (
    <div className={styles.headerContent}>
      <p dangerouslySetInnerHTML={{__html: message}} />
    </div>
  );
}

/**
 * @param {EnterpriseConfiguration} enterpriseConfiguration
 * @returns {React.ReactNode}
 */
function getCardContent(enterpriseConfiguration) {
  const selection = enterpriseConfiguration.entitySelection;

  if (!selection) {
    return undefined;
  } else {
    const name = selection.displayName;
    const count = yext.msgn('1 Entity', '{0} Entities', selection.entityCount);

    return (
      <div className={styles.cardContent}>
        <b>{yext.msg('Saved Filter: ')}</b>
        {`${name} (${count})`}
      </div>
    );
  }
}
